import useSWR, { useSWRConfig } from "swr";

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { TopNavBarContext } from "components/TopNavBar/TopNavBarContext";
import { websocketUrl } from "utils/envVariables";

import ActionCableContext from "../contexts/actionCableContext";
import { baseFetcher } from "../utils/baseFetcher";

import useAuthentication from "./useAuthentication";
import { useChannel } from "./useChannel";

export const SK_CHANNEL = "SkChannel";

/**
 * How long (ms) to wait without receiving *any* data before treating the
 * WebSocket as hung and forcing a full reconnect.
 *
 * ActionCable sends a ping every ~3 s, so 30 s gives plenty of headroom.
 */
const HANG_TIMEOUT_MS = 30_000;

export default function useActionCable() {
  // Track whether we are in the middle of fetching a token + creating a cable.
  const [connecting, setConnecting] = useState(false);

  // Keep a ref version so it's readable inside event-listener closures without
  // causing stale-closure bugs.
  const connectingRef = useRef(false);
  const updateConnecting = (val: boolean) => {
    connectingRef.current = val;
    setConnecting(val);
  };

  const { account: userProfile, mutateAccount } = useAuthentication();

  const { cable, setCable } = useContext(ActionCableContext);

  const {
    setNotificationsReadAt,
    setTotalUnreadNotifications,
    setTotalUnreadMessageGroups,
  } = useContext(TopNavBarContext);

  // ─── Hung-WebSocket watchdog ──────────────────────────────────────────────
  // Reset this timer every time we receive any frame from the server
  // (including ActionCable's regular pings). If it fires, we reconnect.
  const hangTimerRef = useRef<number | undefined>(undefined);

  const clearHangTimer = useCallback(() => {
    window.clearTimeout(hangTimerRef.current);
  }, []);

  // ─── Token fetch + cable creation ────────────────────────────────────────

  const { mutate: mutateWebSocketToken } = useSWR(
    "/api/v2/web-socket/authenticate",
    baseFetcher,
    {
      /**
       * Do not revalidate automatically on focus – we drive reconnects
       * ourselves via the visibility / online events below so that we can
       * avoid race conditions with the `connecting` guard.
       */
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      onSuccess(data) {
        if (!data) return;

        // Guard against double-creation when multiple triggers fire at once.
        if (connectingRef.current) return;

        updateConnecting(true);

        import("@rails/actioncable")
          .then((ActionCable) => {
            const newCable = ActionCable.createConsumer(
              `${websocketUrl}?token=${data?.token}`
            );
            setCable(newCable);
          })
          .catch((err) => {
            console.debug("[ActionCable] Failed to create consumer:", err);
          })
          .finally(() => {
            updateConnecting(false);
          });
      },
      onError(err) {
        console.debug("[ActionCable] Token fetch failed:", err);
        updateConnecting(false);
      },
    }
  );

  // ─── Re-authenticate helper ───────────────────────────────────────────────

  /**
   * Trigger a fresh token fetch → new cable creation.
   * Safe to call multiple times; the `connecting` guard prevents concurrent
   * reconnects, and we debounce tab-focus events to 800 ms.
   */
  const reAuthenticate = useCallback(() => {
    if (connectingRef.current) return;
    console.debug("[ActionCable] Re-authenticating…");
    mutateWebSocketToken();
  }, [mutateWebSocketToken]);

  // ─── Tab visibility reconnect ─────────────────────────────────────────────
  // When the user switches back to the tab the WebSocket may have been dropped
  // by the OS / browser. We wait a short debounce period before reconnecting
  // to avoid hammering the server when the user rapidly alt-tabs.

  const visibilityDebounceRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;

      window.clearTimeout(visibilityDebounceRef.current);
      visibilityDebounceRef.current = window.setTimeout(() => {
        console.debug(
          "[ActionCable] Tab became visible – checking connection…"
        );
        if (cable) {
          // ActionCable's consumer has an internal connection object. If the
          // WebSocket is still open we don't need to reconnect.
          const ws = (cable as any).connection?.webSocket;
          const isOpen =
            ws?.readyState === WebSocket.OPEN ||
            ws?.readyState === WebSocket.CONNECTING;

          if (!isOpen) {
            console.debug(
              "[ActionCable] Connection lost while tab was hidden – reconnecting."
            );
            reAuthenticate();
          }
        } else {
          reAuthenticate();
        }
      }, 800);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearTimeout(visibilityDebounceRef.current);
    };
  }, [cable, reAuthenticate]);

  // ─── Network online reconnect ─────────────────────────────────────────────
  // When the device comes back online (e.g. laptop lid opened, lost Wi-Fi
  // restored) we trigger a reconnect.

  const onlineDebounceRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const handleOnline = () => {
      window.clearTimeout(onlineDebounceRef.current);
      onlineDebounceRef.current = window.setTimeout(() => {
        console.debug("[ActionCable] Network back online – reconnecting.");
        reAuthenticate();
      }, 1_500);
    };

    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.clearTimeout(onlineDebounceRef.current);
    };
  }, [reAuthenticate]);

  // ─── Hung WebSocket watchdog ──────────────────────────────────────────────
  // ActionCable pings the client roughly every 3 s. We reset a 30 s timer
  // each time any message is received. If the timer fires, we assume the
  // socket is hung and force a fresh connection.

  const resetHangTimer = useCallback(() => {
    // clearHangTimer();
    // hangTimerRef.current = window.setTimeout(() => {
    //   // Only reconnect when the tab is visible and we are online.
    //   if (
    //     document.visibilityState === "visible" &&
    //     navigator.onLine !== false
    //   ) {
    //     console.debug(
    //       "[ActionCable] Hang detected (no activity for 30 s) – reconnecting."
    //     );
    //     reAuthenticate();
    //   }
    // }, HANG_TIMEOUT_MS);
  }, []);

  // Start the watchdog once we have a live cable; clear it when cable is gone.
  useEffect(() => {
    if (!cable) {
      clearHangTimer();
      return;
    }
    resetHangTimer();
    return clearHangTimer;
  }, [cable, clearHangTimer, resetHangTimer]);

  // ─── Message handlers ─────────────────────────────────────────────────────

  const { mutate: globalMutate } = useSWRConfig();

  const onNewMessage = useCallback(
    (data: any) => {
      const {
        message_id: _messageId,
        message_group_id: _messageGroupId,
        total_unread_message_groups: totalUnreadMessageGroups,
      } = data;
      setTotalUnreadMessageGroups(totalUnreadMessageGroups);
    },
    [setTotalUnreadMessageGroups]
  );

  const onMessageRead = useCallback(
    (data: any) => {
      const {
        message_group_id: _messageGroupId,
        total_unread_message_groups: totalUnreadMessageGroups,
      } = data;
      setTotalUnreadMessageGroups(totalUnreadMessageGroups);
    },
    [setTotalUnreadMessageGroups]
  );

  const onNewNotification = useCallback(
    (data: any) => {
      const {
        notification_id: notificationId,
        total_unread_notifications: totalUnreadNotifications,
      } = data;

      if (!notificationId) {
        console.debug(
          "[ActionCable] Invalid web socket data for onNewNotification: ",
          data
        );
        return;
      }

      setTotalUnreadNotifications(totalUnreadNotifications);
    },
    [setTotalUnreadNotifications]
  );

  const onNotificationRead = useCallback(
    (data: any) => {
      const notificationsReadAt = new Date(data.notifications_read_at);
      setNotificationsReadAt(notificationsReadAt);
      setTotalUnreadNotifications(0);
    },
    [setNotificationsReadAt, setTotalUnreadNotifications]
  );

  const onNewSearchHistory = useCallback(() => {
    // dispatch({ type: "Search/FETCH_HISTORY" });
  }, []);

  const onAccountDeactivated = useCallback(() => {
    // dispatch(logout("web-socket:account-deactivated"));
  }, []);

  const onAccountUpdated = useCallback(() => {
    // NOTE: event has a payload with: { is_preview_pass: [true, false], is_client: [true, false] }
    mutateAccount();
  }, [mutateAccount]);

  const refreshDiscussionStream = useCallback(() => {
    // dispatch({ type: "List/skListDiscussionStream/REFRESH", ... });
  }, []);

  const refreshMessageGroup = useCallback((_data: any) => {
    // dispatch(loadMessageGroup(messageGroupId));
  }, []);

  const refreshMessageGroups = useCallback(() => {
    // dispatch({ type: "List/skListMessageGroupList/REFRESH", ... });
  }, []);

  // ─── Central dispatch ─────────────────────────────────────────────────────

  const handleReceived = useCallback(
    (room: string, message: any = {}) => {
      // Any message (including pings relayed through subscriptions) resets the
      // hang watchdog, proving the connection is alive.
      resetHangTimer();

      const { event, ...data } = message;
      switch (room) {
        case `private-account-${userProfile?.id}`:
          switch (event) {
            case "new-message":
              onNewMessage(data);
              break;
            case "message-read":
              onMessageRead(data);
              break;
            case "participants-changed":
              refreshMessageGroup(data);
              break;
            case "left-group":
              refreshMessageGroups();
              break;
            case "new-notification":
              onNewNotification(data);
              break;
            case "notifications-read":
              onNotificationRead(data);
              break;
            case "account-deactivated":
              onAccountDeactivated();
              break;
            case "account-updated":
              onAccountUpdated();
              break;
            case "new-search-history":
              onNewSearchHistory();
              break;
            // case "account-device-activated":
            //   getBrowserFingerprint(FINGERPRINT_OPTIONS).then(
            //     ({ fingerprint }) => {
            //       globalMutate(currentAccountDeviceUrl(fingerprint));
            //       mutateAccount();
            //     }
            //   );
            //   break;
            default:
              break;
          }
          break;
        case "private-comments":
          switch (event) {
            case "new-comment":
              refreshDiscussionStream();
              break;
            default:
              break;
          }
          break;
        default:
          break;
      }
    },
    [
      onAccountDeactivated,
      onAccountUpdated,
      onMessageRead,
      onNewMessage,
      onNewNotification,
      onNewSearchHistory,
      onNotificationRead,
      refreshDiscussionStream,
      refreshMessageGroup,
      refreshMessageGroups,
      resetHangTimer,
      userProfile?.id,
    ]
  );

  // ─── Per-channel received callbacks ──────────────────────────────────────

  const privateCommentOnReceived = useCallback(
    (message: any) => handleReceived("private-comments", message),
    [handleReceived]
  );

  const privateAccountOnReceived = useCallback(
    (message: any) =>
      handleReceived(`private-account-${userProfile?.id}`, message),
    [handleReceived, userProfile?.id]
  );

  const platformUpdatesOnReceived = useCallback(
    (message: any) => handleReceived("platform-updates", message),
    [handleReceived]
  );

  // ─── Channel subscriptions ────────────────────────────────────────────────

  const privateCommentsChannel = useMemo(
    () => ({ channel: SK_CHANNEL, room: "private-comments" }),
    []
  );

  const privateAccountChannel = useMemo(
    () => ({
      channel: SK_CHANNEL,
      room: `private-account-${userProfile?.id}`,
    }),
    [userProfile?.id]
  );

  const platformUpdatesChannel = useMemo(
    () => ({ channel: SK_CHANNEL, room: "platform-updates" }),
    []
  );

  useChannel({
    key: "useActionCable",
    name: "private-comments",
    channel: privateCommentsChannel,
    onReceived: privateCommentOnReceived,
  });

  useChannel({
    key: "useActionCable",
    name: `private-account-${userProfile?.id}`,
    channel: privateAccountChannel,
    onReceived: privateAccountOnReceived,
    /**
     * When the server drops the private-account channel we re-authenticate
     * so a fresh token is used for the new cable. Wrapping in a small delay
     * avoids a tight reconnect loop during transient network hiccups.
     */
    onDisconnected: reAuthenticate,
  });

  useChannel({
    key: "useActionCable",
    name: "platform-updates",
    channel: platformUpdatesChannel,
    onReceived: platformUpdatesOnReceived,
  });

  return { connecting };
}
